import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { BACKEND, getToken } from "./api";

const RtCtx = createContext({ connected: false, subscribe: () => () => {} });

export function RealtimeProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const subs = useRef(new Set());

  useEffect(() => {
    let ws;
    let retry;
    let ping;
    let closed = false;
    const connect = () => {
      const token = getToken();
      if (!token) return;
      ws = new WebSocket(`${BACKEND.replace(/^http/, "ws")}/api/ws?token=${encodeURIComponent(token)}`);
      ws.onopen = () => {
        setConnected(true);
        ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
      };
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          const msg = JSON.parse(e.data);
          subs.current.forEach((fn) => fn(msg));
        } catch {
          /* ignore malformed */
        }
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(ping);
        if (!closed) retry = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      clearInterval(ping);
      if (ws) ws.close();
    };
  }, []);

  const subscribe = useCallback((fn) => {
    subs.current.add(fn);
    return () => subs.current.delete(fn);
  }, []);

  return <RtCtx.Provider value={{ connected, subscribe }}>{children}</RtCtx.Provider>;
}

export const useRealtime = () => useContext(RtCtx);

// Fetch on deps change, on realtime events, and poll (fast when socket is down).
export function useLiveData(fetcher, deps) {
  const { connected, subscribe } = useRealtime();
  const ref = useRef(fetcher);
  ref.current = fetcher;

  useEffect(() => {
    ref.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    let t;
    const un = subscribe((m) => {
      if (m.type !== "queue" && m.type !== "notification") return;
      clearTimeout(t);
      t = setTimeout(() => ref.current(), 250);
    });
    return () => {
      un();
      clearTimeout(t);
    };
  }, [subscribe]);

  useEffect(() => {
    const id = setInterval(() => ref.current(), connected ? 20000 : 4000);
    return () => clearInterval(id);
  }, [connected]);

  return connected;
}
